/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _omit from "lodash/omit";

import shareAllotmentDataService from "./../../services/shareCapitalAllotment";

// --- THUNKS ---

export const addShareAllotmentAsync = createAsyncThunk(
  "shareAllotment/addShareAllotmentAsync",
  async (data, { rejectWithValue }) => {
    try {
      const res = await shareAllotmentDataService.create(data);
      if (!res.status) {
        return rejectWithValue(
          res.message || "Failed to create share allotment"
        );
      }
      return res;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

export const updateShareAllotmentAsync = createAsyncThunk(
  "shareAllotment/updateShareAllotmentAsync",
  async (data) => {
    const res = await shareAllotmentDataService.update(
      data._id,
      _omit(data, [
        "_id",
        "_v",
        "createdAt",
        "createdBy",
        "updatedAt",
        "rightsAttached",
      ])
    );
    return res.data;
  }
);

export const deleteShareAllotmentAsync = createAsyncThunk(
  "shareAllotment/deleteShareAllotmentAsync",
  async (data) => {
    const res = await shareAllotmentDataService.remove(data?.id);
    return res.data;
  }
);

export const getShareAllotmentsAsync = createAsyncThunk(
  "shareAllotment/getShareAllotmentsAsync",
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await shareAllotmentDataService.getList(companyId);

      if (!res) {
        return rejectWithValue(
          "Failed to fetch share allotments: Invalid response structure"
        );
      }
      const allotments = res.shareAllotments.items;
      const totalCount = res.shareAllotments.totalCount;
      return {
        items: allotments,
        totalCount: totalCount,
      };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

export const submitAllotmentsAsync = createAsyncThunk(
  "shareAllotment/submitAllotmentsAsync",
  async (data, { rejectWithValue }) => {
    try {
      const res = await shareAllotmentDataService.submitAllotments(data);
      if (!res.status) {
        return rejectWithValue(res.message || "Failed to submit allotments");
      }
      return res;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

// New thunks for Pro-rata, Rights Issue, and General Offer
export const applyProRataAsync = createAsyncThunk(
  "shareAllotment/applyProRataAsync",
  async (data, { rejectWithValue }) => {
    try {
      const res = await shareAllotmentDataService.createProRata(data);
      if (!res.status) {
        return rejectWithValue(
          res.message || "Failed to create pro-rata offer"
        );
      }
      return res;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

export const applyRightsIssueAsync = createAsyncThunk(
  "shareAllotment/applyRightsIssueAsync",
  async (data, { rejectWithValue }) => {
    try {
      const res = await shareAllotmentDataService.createRightsIssue(data);
      if (!res.status) {
        return rejectWithValue(res.message || "Failed to create rights issue");
      }
      return res;
    } catch (error) {
      console.error("Error:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

export const applyGeneralOfferAsync = createAsyncThunk(
  "shareAllotment/applyGeneralOfferAsync",
  async (data, { rejectWithValue }) => {
    try {
      const res = await shareAllotmentDataService.createGeneralOffer(data);
      if (!res.status) {
        return rejectWithValue(res.message || "Failed to create general offer");
      }
      return res;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred.";
      return rejectWithValue(message);
    }
  }
);

// --- SLICE ---
const shareAllotmentSlice = createSlice({
  name: "Share Allotment Table",
  initialState: {
    activeTab: "",
    allotments: [],
    totalCount: 0,
    preEmptionRights: null,
    proRata: null, // To manage pro-rata offer data
    rightsIssue: null, // To manage rights issue offer data
    generalOffer: null, // To manage general offer data
  },
  reducers: {
    addAllotmentShare: (state, action) => {
      state.allotments.push(action.payload.allotmentShare);
    },
    updateAllotmentShare: (state, { payload: { allotmentShare } }) => {
      const id = allotmentShare._id;
      const index = state.allotments.findIndex((item) => id === item._id);
      if (index > -1) state.allotments.splice(index, 1, allotmentShare);
    },
    deleteAllotmentShare: (state, { payload }) => {
      const index = state.allotments.findIndex(
        (item) => payload._id === item._id
      );
      if (index > -1) state.allotments.splice(index, 1);
    },
    updateAllotmentSharesSlice: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setAllotmentShares: (state, action) => {
      state.allotments = action.payload;
    },
    resetAllotmentSharesTable: (state) => {
      state.allotments = [];
      state.totalCount = 0;
      state.preEmptionRights = null;
      state.proRata = null; // Reset proRata
      state.rightsIssue = null; // Reset rightsIssue
      state.generalOffer = null; // Reset generalOffer
    },
    setPreEmptionRights: (state, action) => {
      state.preEmptionRights = action.payload;
    },
    resetPreEmptionRights: (state) => {
      state.preEmptionRights = null;
    },
    // New reducers for Pro-rata, Rights Issue, and General Offer
    setProRata: (state, action) => {
      state.proRata = action.payload;
    },
    resetProRata: (state) => {
      state.proRata = null;
    },
    setRightsIssue: (state, action) => {
      state.rightsIssue = action.payload;
    },
    resetRightsIssue: (state) => {
      state.rightsIssue = null;
    },
    setGeneralOffer: (state, action) => {
      state.generalOffer = action.payload;
    },
    resetGeneralOffer: (state) => {
      state.generalOffer = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addShareAllotmentAsync.fulfilled, (state, action) => {
        const newAllotment = action.payload.allotment;
        if (newAllotment) {
          state.allotments.push(newAllotment);
          state.totalCount += 1;
        }
      })
      .addCase(updateShareAllotmentAsync.fulfilled, (state, { payload }) => {
        const index = state.allotments.findIndex(
          (item) => payload._id === item._id
        );
        if (index > -1) {
          state.allotments.splice(index, 1, { ...payload });
        }
      })
      .addCase(deleteShareAllotmentAsync.fulfilled, (state, { payload }) => {
        const index = state.allotments.findIndex(
          (item) => item._id === payload.id
        );
        if (index > -1) {
          state.allotments.splice(index, 1);
          state.totalCount -= 1;
        }
      })
      .addCase(getShareAllotmentsAsync.fulfilled, (state, action) => {
        state.allotments = action.payload.items;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(getShareAllotmentsAsync.rejected, () => {
        // Error handling if needed
      })
      .addCase(submitAllotmentsAsync.fulfilled, (state, action) => {
        if (action.payload.updatedAllotments) {
          state.allotments = action.payload.updatedAllotments;
        }
      })
      .addCase(submitAllotmentsAsync.rejected, (state, action) => {
        if (process.env.NODE_ENV === "development") {
          console.error(
            "[shareCapitalAllotment] Error submitting allotments:",
            action.payload
          );
        }
      })
      .addCase(applyProRataAsync.fulfilled, (state, action) => {
        state.proRata = action.payload.data;
      })
      .addCase(applyProRataAsync.rejected, (state, action) => {
        if (process.env.NODE_ENV === "development") {
          console.error(
            "[shareCapitalAllotment] Error creating pro-rata offer:",
            action.payload
          );
        }
      })
      .addCase(applyRightsIssueAsync.fulfilled, (state, action) => {
        state.rightsIssue = action.payload.data;
      })
      .addCase(applyRightsIssueAsync.rejected, (state, action) => {
        if (process.env.NODE_ENV === "development") {
          console.error(
            "[shareCapitalAllotment] Error creating rights issue:",
            action.payload
          );
        }
      })
      .addCase(applyGeneralOfferAsync.fulfilled, (state, action) => {
        state.generalOffer = action.payload.data;
      })
      .addCase(applyGeneralOfferAsync.rejected, (state, action) => {
        if (process.env.NODE_ENV === "development") {
          console.error(
            "[shareCapitalAllotment] Error creating general offer:",
            action.payload
          );
        }
      });
  },
});

export const {
  addAllotmentShare,
  updateAllotmentShare,
  deleteAllotmentShare,
  updateAllotmentSharesSlice,
  setAllotmentShares,
  resetAllotmentSharesTable,
  setPreEmptionRights,
  resetPreEmptionRights,
  setProRata,
  resetProRata,
  setRightsIssue,
  resetRightsIssue,
  setGeneralOffer,
  resetGeneralOffer,
} = shareAllotmentSlice.actions;

export default shareAllotmentSlice.reducer;
